import './Styles.css'
import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useQueryRequest} from './core/QueryRequestProvider'
import {useListView} from './core/ListViewProvider'
import {QueryRequestProvider} from './core/QueryRequestProvider'
import {getStatus} from './core/_requests'
import {QueryResponseProvider, useQueryResponse} from './core/QueryResponseProvider'
import {ID} from '../../../../../../_metronic/helpers'
import {CustomDatePicker} from '../../../../widgets/components/Datepicker'
import {DataTable} from './table/Table'
import {ListGrouping} from './components/header/ListGrouping'
import {TicketProvider} from './core/ListViewProvider'
import {TicketForm} from './form/TicketForm'
import {Tab, Nav, Card, Button} from 'react-bootstrap'
import * as Lucide from 'lucide-react'
import {Plus, X} from 'lucide-react'
import {Data as Ticket} from './core/_models'

interface TicketPageProps {
  externalTicketTrigger?: {phone: string; timestamp: number} | null
  onTriggerProcessed?: () => void
}

type TabType = 'list' | 'add' | 'edit' | 'report'

interface TabItem {
  id: string
  title: string
  isClosable: boolean
  type: TabType
  ticketId?: ID
}
const formatLocal = (d: Date) => {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
const List = ({
  handleAddTab,
  openEditTab,
}: {
  handleAddTab: () => void
  openEditTab: (data: {id: ID; title: string}) => void
}) => {
  const {state, updateState} = useQueryRequest()
  useEffect(() => {
    const currentFilter = (state.filter || {}) as any
    const today = new Date();
    if (!currentFilter.startDate) {
      updateState({
        filter: {
          ...currentFilter,
          startDate: formatLocal(today),
          endDate: formatLocal(today),
        },
        page: 1,
      })
    }
  }, [])
  const [status, setStatus] = useState<Array<{id: string; name: string}>>([])
  const {handleAddTabs, selected} = useListView() as any
  const {response} = useQueryResponse()

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const statusResponse = await getStatus()
        setStatus(statusResponse.data)
      } catch (error) {
        console.error('Failed to fetch initial data:', error)
      }
    }
    fetchInitialData()
  }, [])

  const stats = useMemo(() => {
    const r = response as any

    return {
      total: response?.payload?.stats?.total || 0,
      open: response?.payload?.stats?.open || 0,
      closed: response?.payload?.stats?.closed || 0,
      assign: response?.payload?.stats?.assign || 0,
    }
  }, [response])

  const updateFilter = (key: string, value: any) => {
    const currentFilter = (state.filter || {}) as {[key: string]: any}
    updateState({
      filter: { ...currentFilter, [key]: value },
      page: 1,
    })
  }

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    const currentFilter = (state.filter || {}) as {[key: string]: any}
    
    updateState({
      filter: {
        ...currentFilter,
        startDate: start ? formatLocal(start) : undefined,
        endDate: end ? formatLocal(end) : undefined,
      },
      page: 1,
    })
  }

  const handleGlobalSearch = (value: string) => {
    updateState({ ...state, search: value, page: 1 })
  }

  const getDateRangeFromState = () => {
    const filter = state.filter as {[key: string]: any} | undefined
    return {
      startDate: filter?.startDate ? new Date(filter.startDate) : null,
      endDate: filter?.endDate ? new Date(filter.endDate) : null,
    }
  }

  const pillConfigs = [
    { label: 'Total Ticket', value: '', count: stats.total, icon: Lucide.LayoutDashboard, color: '#005AAA' },
    { label: 'Open', value: 'open', count: stats.open, icon: Lucide.CircleDot, color: '#c21a17' },
    { label: 'Closed', value: 'closed', count: stats.closed, icon: Lucide.CheckCircle2, color: '#30e320' },
    { label: 'Assign', value: 'assign', count: stats.assign, icon: Lucide.UserCog, color: '#e8cb0e' },
  ]

  return (
    <div className='ticket-page-wrapper'>
      <div className='glass-card monitoring-header position-relative'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <h1>Ticket Management</h1>
            <p>Monitor and manage customer support tickets efficiently.</p>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <div className='connection-status-indicator connected'>
              <span className='indicator-dot pulse-glow'></span>
              System Online
            </div>
            <button className='btn btn-primary' onClick={handleAddTab}>
              <i className='bi bi-plus-circle me-2'></i>
              Add Ticket
            </button>
          </div>
        </div>
      </div>
      <div className='row g-4 mb-6 px-2'>
        {pillConfigs.map((pill, idx) => {
          const currentStatus = ((state.filter || {}) as any).status
          const isActive = currentStatus === pill.value || (pill.value === '' && !currentStatus)
          const Icon = pill.icon

          return (
            <div key={idx} className='col-md-3'>
              <div 
                onClick={() => updateFilter('status', pill.value || undefined)}
                className={`status-pill-card p-4 d-flex align-items-center cursor-pointer transition-all ${isActive ? 'active' : ''}`}
                style={{ 
                  backgroundColor: isActive ? pill.color : '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: isActive ? `1px solid ${pill.color}` : '1px solid #eee'
                }}
              >
                <div className='rounded-circle d-flex align-items-center justify-content-center me-3'
                  style={{ 
                    width: '40px', height: '40px', 
                    backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : `${pill.color}10` 
                  }}>
                  {Icon && <Icon size={20} color={isActive ? '#fff' : pill.color} />}
                </div>
                <div>
                  <div className={`fw-bold fs-7 ${isActive ? 'text-white opacity-75' : 'text-muted'}`}>
                    {pill.label}
                  </div>
                  <div className={`fw-bolder fs-4 ${isActive ? 'text-white' : 'text-dark'}`}>
                    {Number(pill.count).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className='glass-card search-filter-container'>
        {selected.length > 0 ? (
          <ListGrouping />
        ) : (
          <>
            <div className='row g-3 mb-3'>
              <div className='col-6'>
                <div className='input-group'>
                  <span className='input-group-text'><i className='bi bi-search'></i></span>
                  <input
                    type='text'
                    className='form-control modern-search-input'
                    placeholder='Search ...'
                    value={state.search || ''}
                    onChange={(e) => handleGlobalSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            
            <div className='row g-3 align-items-end'>
              <div className='col-lg-4 col-md-6'>
                <CustomDatePicker dateRange={getDateRangeFromState()} onDateChange={handleDateChange} />
              </div>
              <div className='col-lg-2 col-md-3 col-sm-6'>
                <select
                  className='form-select'
                  value={((state.filter || {}) as any).status || ''}
                  onChange={(e) => updateFilter('status', e.target.value)}
                >
                  <option value=''>All Statuses</option>
                  {status.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className='col-lg-2 col-md-3 col-sm-6'>
                <select
                  className='form-select'
                  value={state.items_per_page}
                  onChange={(e) => updateState({ items_per_page: Number(e.target.value) as any, page: 1 })}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className='col-lg-4 col-md-12 d-flex gap-2 justify-content-lg-end'>
                <button 
                  className='btn btn-primary flex-grow-1 flex-lg-grow-0' 
                  onClick={() => updateState({...state, page: 1})}
                  title='Apply Filters'
                >
                  <i className='bi bi-funnel-fill d-none d-sm-inline me-1'></i>
                  <span className='d-none d-sm-inline'>Apply</span>
                  <i className='bi bi-funnel-fill d-sm-none'></i>
                </button>
                <button 
                  className='btn btn-warning flex-grow-1 flex-lg-grow-0' 
                  onClick={() => updateState({search: '', filter: {}, page: 1})}
                  title='Clear Filters'
                >
                  <i className='bi bi-x-circle-fill d-none d-sm-inline me-1'></i>
                  <span className='d-none d-sm-inline'>Clear</span>
                  <i className='bi bi-x-circle-fill d-sm-none'></i>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className='glass-card mt-6'>
        <DataTable />
      </div>
    </div>
  )
}


const ListWrapper = ({externalTicketTrigger, onTriggerProcessed}: TicketPageProps) => {
  const [openTabs, setOpenTabs] = useState<TabItem[]>([
    {
      id: 'ticket-list',
      title: 'Daftar Tiket',
      isClosable: false,
      type: 'list',
    },
  ])
  const [activeTab, setActiveTab] = useState<string>('ticket-list')
  const handleCloseTab = useCallback((tabId: string) => {
    if (tabId === 'ticket-list') return
    setOpenTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== tabId))
    setActiveTab('ticket-list')
    
    sessionStorage.removeItem(`pop_phone_${tabId}`)
  }, [])

  const getNextAddNumber = (tabs: TabItem[]): number => {
    const addTabNumbers = tabs
      .map((tab) => {
        const match = tab.title.match(/^Untitled-(\d+)$/)
        return match ? parseInt(match[1], 10) : 0
      })
      .filter((num) => num > 0)
      .sort((a, b) => a - b)

    let nextNumber = 1
    for (const num of addTabNumbers) {
      if (nextNumber < num) break
      nextNumber = num + 1
    }
    return nextNumber
  }
  const handleAddTab = useCallback(() => {
    const nextAddNumber = getNextAddNumber(openTabs)
    const newTabId = `ticket-add-${nextAddNumber}`
    const newTab: TabItem = {
      id: newTabId,
      title: `Untitled-${nextAddNumber}`,
      isClosable: true,
      type: 'add',
    }
    setOpenTabs((prevTabs) => [...prevTabs, newTab])
    setActiveTab(newTab.id)
  }, [openTabs])
  
  const handleUpdateSuccess = useCallback((updatedTicket: Ticket) => {
    if (!updatedTicket.id) return
    const tabId = `ticket-edit-${updatedTicket.id}`
    setOpenTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== tabId))
    setActiveTab('ticket-list')
  }, [])

  const handleSaveSuccess = useCallback(
    (addTabId: string, newTicket: Ticket) => {
      if (!newTicket.id) return
      setOpenTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== addTabId))
      setActiveTab('ticket-list')
      sessionStorage.removeItem(`pop_phone_${addTabId}`)
    },
    []
  )

  const openEditTab = useCallback((data: {id: ID; title: string}) => {
    const tabId = `ticket-edit-${data.id}`
    const existingTab = openTabs.find((tab) => tab.id === tabId)
    if (existingTab) {
      setActiveTab(tabId)
      return
    }
    const newTab: TabItem = {
      id: tabId,
      title: `Edit: ${data.title.substring(0, 20)}${data.title.length > 20 ? '...' : ''}`,
      isClosable: true,
      type: 'edit',
      ticketId: data.id,
    }
    setOpenTabs((prevTabs) => [...prevTabs, newTab])
    setActiveTab(tabId)
  }, [openTabs])

  useEffect(() => {
    if (externalTicketTrigger && externalTicketTrigger.phone) {
      const phone = externalTicketTrigger.phone;
      const ts = externalTicketTrigger.timestamp;
      const newTabId = `ticket-add-pop-${ts}`;

      setOpenTabs((prev) => {
        const isExist = prev.find((t) => t.id === newTabId);
        if (isExist) return prev;

        return [
          ...prev,
          {
            id: newTabId,
            title: `Call: ${phone}`,
            isClosable: true,
            type: 'add',
          },
        ];
      });

      setActiveTab(newTabId);
      sessionStorage.setItem(`pop_phone_${newTabId}`, phone);

      if (onTriggerProcessed) {
        onTriggerProcessed();
      }
    }
  }, [externalTicketTrigger, onTriggerProcessed]);

  const renderTabContent = (tab: TabItem) => {
    switch (tab.type) {
      case 'list':
        return <List handleAddTab={handleAddTab} openEditTab={openEditTab} />
      case 'add':
        return (
          <div className='px-8 py-5'>
            <TicketForm
              initialPhoneNumber={sessionStorage.getItem(`pop_phone_${tab.id}`) || ''}
              onSaveSuccess={(newTicket) => handleSaveSuccess(tab.id, newTicket)}
              onCancel={() => handleCloseTab(tab.id)}
            />
          </div>
        )
      case 'edit':
        return (
          <div className='px-8 py-5'>
            <TicketForm
              id={tab.ticketId}
              onSaveSuccess={handleUpdateSuccess}
              onCancel={() => handleCloseTab(tab.id)}
            />
          </div>
        )
      default:
        return null
    }
  }
  

  return (
    <QueryRequestProvider>
      <QueryResponseProvider>
        <TicketProvider 
          handleAddTab={handleAddTab} 
          openEditTab={openEditTab}
        >
          <Card className='mb-5 mb-xl-8'>
            <Tab.Container id='ticket-tab-container' activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
              <Card.Header className='border-0 pt-5'>
                <Nav variant='tabs' className='flex-nowrap overflow-auto'>
                  {openTabs.map((tab) => (
                    <Nav.Item key={tab.id}>
                      <Nav.Link eventKey={tab.id} className='d-flex align-items-center text-nowrap'>
                        {tab.title}
                        {tab.isClosable && (
                          <Button
                            variant='link'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCloseTab(tab.id)
                            }}
                            className='p-0 ms-2 lh-1'
                          >
                            &times;
                          </Button>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                  <Nav.Item className='d-flex align-items-center'>
                    <Button 
                      variant='link' 
                      className='nav-link d-flex align-items-center justify-content-center mb-6 ms-2' 
                      onClick={handleAddTab}
                      style={{
                        width: '35px', 
                        height: '35px', 
                        minWidth: '35px', 
                        borderRadius: '8px',
                      }}
                    >
                      <Plus size={20} strokeWidth={2.5} />
                    </Button>
                  </Nav.Item>
                </Nav>
              </Card.Header>
              <Card.Body className='py-0 px-0'>
                <Tab.Content>
                  {openTabs.map((tab) => (
                    <Tab.Pane key={tab.id} eventKey={tab.id}>
                      {renderTabContent(tab)}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Card.Body>
            </Tab.Container>
          </Card>
        </TicketProvider>
      </QueryResponseProvider>
    </QueryRequestProvider>
  )
}

export {ListWrapper}